import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  Cell,
} from "recharts";

export type CountsDto = {
  MappedCDRC: number;
  NotMappedCDRC: number;
  TotalRows: number;
  ExcetionCDRC: number;
};
export type BarData = { name: string; count: number; color?: string };

const BAR_COLORS = ["#1E3A8A", "#60A5FA", "OC77BD"];

const LoadingPlaceholder: React.FC<{ height: number }> = ({ height }) => (
  <div
    className="h-full flex items-center justify-center text-sm text-gray-400"
    style={{ height }}
  >
    <svg
      className="animate-spin h-5 w-5 mr-2 text-gray-400"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
    Loading...
  </div>
);

const EmptyContainer: React.FC<{ height: number }> = ({ height }) => (
  <div style={{ height }} className="h-full" />
);

const buildDataFromCounts = (counts?: CountsDto | null): BarData[] => {
  if (!counts) return [];

  const mapped = Number(counts?.MappedCDRC ?? 0);
  const notMapped = Number(counts?.NotMappedCDRC ?? 0);
  const exception = Number(counts?.ExcetionCDRC ?? 0);
  const total = Number(counts?.TotalRows ?? mapped + notMapped + exception);

  if (total === 0 && mapped === 0 && notMapped === 0) return [];

  return [
    { name: "Mapped", count: mapped, color: BAR_COLORS[0] },
    { name: "Not Mapped", count: notMapped, color: BAR_COLORS[1] },
    { name: "Exception", count: exception, color: BAR_COLORS[2] },
  ];
};

const CDRCMappingBarChart: React.FC<{
  counts?: CountsDto | null;
  data?: BarData[];
  height?: number;
  loading?: boolean;
}> = ({ counts = null, data: dataProp, height = 180, loading = false }) => {
  const data: BarData[] = Array.isArray(dataProp)
    ? dataProp
    : buildDataFromCounts(counts);

  if (loading) return <LoadingPlaceholder height={height} />;
  if (!data?.length) return <EmptyContainer height={height} />;

  const renderTooltip = (props: {
    active?: boolean;
    payload?: any[];
    label?: string | number;
  }) => {
    const { active, payload, label } = props ?? {};
    if (!active || !payload?.length) return null;

    const item = payload[0];
    const idx = item?.payload?.index ?? item?.index;
    const matchedByIndex = typeof idx === "number" ? data[idx] : undefined;

    const payloadName = item?.payload?.name ?? item?.name ?? label;
    const matchedByName = !matchedByIndex
      ? data.find((d) => d.name === payloadName)
      : undefined;

    const matched = matchedByIndex ?? matchedByName;

    const valueRaw = matched?.count ?? item?.payload?.value ?? item?.value ?? 0;
    const nameRaw =
      matched?.name ??
      item?.payload?.displayName ??
      item?.payload?.name ??
      item?.name ??
      label ??
      "Value";
    const color = matched?.color ?? item?.payload?.color ?? item?.color;

    return (
      <div
        className="bg-white border border-gray-200 rounded-md shadow-sm p-2"
        style={{ pointerEvents: "none", minWidth: 88 }}
      >
        <div className="text-lg font-semibold text-center">{valueRaw}</div>
        <div className="text-sm mt-1 text-center" style={{ color }}>
          {color ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full"
                style={{ background: color, display: "inline-block" }}
              />
              <span>{nameRaw}</span>
            </span>
          ) : (
            <span>{nameRaw}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 12, right: 8, left: 4, bottom: 8 }}
        >
          <CartesianGrid vertical={false} stroke="#EEF2F7" />
          <YAxis tick={{ fontSize: 10, fill: "#4B5563" }} />
          <Tooltip
            cursor={{ fill: "rgba(2,132,199,0.08)" }}
            content={renderTooltip}
          />
          <Bar
            dataKey="count"
            minPointSize={(value) => (value > 0 ? 5 : 0)}
            radius={[8, 8, 0, 0]}
          >
            <LabelList
              dataKey="count"
              position="top"
              className="text-[11px]"
              style={{ fontWeight: 700 }}
            />
            {data.map((entry, idx) => (
              <Cell
                key={`cell-${idx}`}
                fill={entry.color ?? BAR_COLORS[idx % BAR_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CDRCMappingBarChart;
